import { Loader } from '@zorro/design-system/v2/Loader';
import { formatToMMMDayYear } from '@zorro/shared/formatters';
import {
  Center,
  CurrencyInput,
  FormErrorMessage,
  Grid,
  Stack,
  Text,
} from '@zorro/web/design-system';
import {
  CoveragePeriodInputs,
  ReadonlyInsuredForm,
  useAnalytics,
  useCoveragePeriodForm,
  useEnrollmentInsuredForm,
  useHydrateEnrollmentAllowanceStoreFromBenefits,
  usePremiumAmountForm,
} from '@zorro/web/shared-ui';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import { Controller } from 'react-hook-form';

import { EmployeePageTabType } from '../../EmployeePage/EmployeePageContent';
import {
  CarrierApplicationModalStepType,
  CarrierApplicationStep,
} from '../CarrierApplicationModal';
import { ElectedPlanCard } from './ElectedPlanCard';

type Props = {
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  benefitId?: string;
  onSubmit: () => void;
  tab: EmployeePageTabType;
};

export const CarrierApplicationEnrollmentInfoStep = ({
  employerId,
  employeeId,
  onboardingPeriodId,
  benefitId,
  onSubmit,
  tab,
}: Props) => {
  const { coveragePeriodForm, onSubmit: onSubmitEffectiveDates } =
    useCoveragePeriodForm({
      employeeId,
      onboardingPeriodId,
      shouldShowLoadingOverlay: false,
    });

  const { data: benefitsData } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: [onboardingPeriodId],
  });

  const insuredForm = useEnrollmentInsuredForm({
    onboardingPeriodId,
    employeeId,
    employerId,
    isFinalizationMode: false,
    isEmployeeMode: false,
  });

  const { premiumAmountForm, onSubmit: onSubmitPremium } = usePremiumAmountForm(
    {
      mode: 'fetch',
      employeeId,
      onboardingPeriodId,
      benefitId,
      shouldShowLoadingOverlay: false,
    }
  );
  const { control: premiumControl, formState: premiumFormState } =
    premiumAmountForm;
  const premium = premiumAmountForm.watch('premium');
  const { sendEvent } = useAnalytics();

  useHydrateEnrollmentAllowanceStoreFromBenefits(
    benefitsData,
    benefitId,
    premium
  );

  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindOne',
    params: [employeeId, onboardingPeriodId],
  });

  const handleSubmit = async () => {
    const { effectiveFrom, effectiveUntil } = coveragePeriodForm.getValues();

    if (!effectiveFrom || !effectiveUntil) {
      return { navigate: false };
    }

    const datesResult = await onSubmitEffectiveDates();
    if (!datesResult) {
      return { navigate: false };
    }

    const premiumResult =
      (await premiumAmountForm.trigger()) && (await onSubmitPremium());

    if (premiumResult) onSubmit();

    return { navigate: premiumResult };
  };

  if (!employee || !onboardingPeriod) {
    return (
      <Center mih="calc(100vh - 18.5rem)">
        <Loader />
      </Center>
    );
  }

  return (
    <CarrierApplicationStep
      step={CarrierApplicationModalStepType.ENROLLMENT_INFO}
      employerId={employerId}
      employeeId={employeeId}
      onboardingPeriodId={onboardingPeriodId}
      benefitId={benefitId}
      onSubmit={handleSubmit}
      submitLabel="Confirm and continue"
      withReviewCard
      tab={tab}
    >
      <Stack gap="md">
        <Text fw={600}>Elected plan</Text>
        <ElectedPlanCard
          onboardingPeriodId={onboardingPeriodId}
          benefitId={benefitId}
        />
      </Stack>

      <Stack gap="md">
        <Text fw={600}>Who is covered</Text>
        <ReadonlyInsuredForm
          insuredForm={insuredForm}
          onboardingPeriodId={onboardingPeriodId}
          withAgeAtPlanStart
          accordionVariant="shadow"
        />
      </Stack>

      <Stack gap="md">
        <Stack gap={0}>
          <Text fw={600}>Premium amount</Text>
          <Text size="sm" c="var(--color-text-default-secondary)">
            Verify or update the premium amount based on what the carrier is
            reflecting
          </Text>
        </Stack>
        <Grid>
          <Grid.Col span={5}>
            <Stack gap="xs">
              <Controller
                control={premiumControl}
                name="premium"
                render={({ field: { ...rest } }) => (
                  <CurrencyInput
                    {...rest}
                    decimalScale={2}
                    prefix="$"
                    size="md"
                    onBlur={async () => {
                      if (premiumFormState.defaultValues?.premium !== premium) {
                        await sendEvent('edit_premium', {
                          onboarding_period_id: onboardingPeriodId,
                          from_premium:
                            premiumFormState.defaultValues?.premium || 0,
                          to_premium: premium,
                        });
                      }
                    }}
                  />
                )}
              />
              <FormErrorMessage
                errors={premiumFormState.errors}
                fieldName="premium"
              />
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>

      <Stack gap="md">
        <Stack gap={0}>
          <Text fw={600}>Requested coverage period</Text>
          <Text size="sm" c="var(--color-text-default-secondary)">
            Verify the requested coverage period
          </Text>
        </Stack>
        {benefitsData?.majorMedicalBenefit ? (
          <CoveragePeriodInputs
            employeeId={employeeId}
            onboardingPeriodId={onboardingPeriodId}
            coveragePeriodForm={coveragePeriodForm}
          />
        ) : (
          <Text>
            {`${formatToMMMDayYear(onboardingPeriod.effectiveFrom)} – ${formatToMMMDayYear(onboardingPeriod.effectiveUntil)}`}
          </Text>
        )}
      </Stack>
    </CarrierApplicationStep>
  );
};
